import React, { useState } from "react";
import {
  Button,
  Drawer,
  Stack,
  TextField,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
  linkRestaurantInvoiceToHotelInvoice,
  listRestaurantInvoiceDetails,
  searchHotelInvoices,
} from "../../../store/actions";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

const LinkBookingInvoiceButton = ({ colors }) => {
  const { id } = useParams();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    invoice: null,
  });

  const [bookingId, setBookingId] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [checkinDate, setCheckinDate] = useState("");
  const [checkoutDate, setCheckoutDate] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔹 Reset all state when Drawer closes
  const handleCloseDrawer = () => {
    setOpen(false);
    setBookingId("");
    setRoomNumber("");
    setCheckinDate("");
    setCheckoutDate("");
    setResults([]);
    setConfirmDialog({ open: false, invoice: null });
    setLoading(false);
  };

  const handleFilter = async () => {
    if (
      checkoutDate &&
      checkinDate &&
      moment(checkoutDate).isBefore(moment(checkinDate))
    ) {
      toast.warning("Checkout date cannot be earlier than check-in date.");
      return;
    }

    setLoading(true);
    try {
      const res = await dispatch(
        searchHotelInvoices({
          booking_id: bookingId || null,
          room_number: roomNumber || null,
          checkin_date: checkinDate ? moment(checkinDate).unix() : null,
          checkout_date: checkoutDate ? moment(checkoutDate).unix() : null,
        })
      );
      setResults(res || []);
    } catch (error) {
      console.error("Error searching invoices:", error);
      toast.error("Failed to fetch invoices. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (invoice) => {
    setConfirmDialog({ open: true, invoice });
  };

  const confirmLink = async () => {
    const { invoice } = confirmDialog;
    try {
      const res = await dispatch(
        linkRestaurantInvoiceToHotelInvoice({
          hotelInvoiceId: invoice._id,
          restaurantInvoiceId: id,
        })
      );
      toast.success(`Invoice ${invoice.inv_no} linked successfully!`);
      dispatch(listRestaurantInvoiceDetails({ id }));
      handleCloseDrawer();
    } catch (err) {
      toast.error("Failed to link booking invoice.");
    } finally {
      setConfirmDialog({ open: false, invoice: null });
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="info"
        // color={colors.blueAccent[700]}
        startIcon={<LinkIcon />}
        onClick={() => setOpen(true)}
      >
        Link Booking Invoice
      </Button>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460 },
            p: 2,
            bgcolor: theme.palette.background.default,
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            Find Booking Invoice
          </Typography>
          <IconButton onClick={handleCloseDrawer}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Filters */}
        <Grid container spacing={1} mb={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Booking ID"
              size="small"
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Room Number"
              size="small"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Check-in After"
              type="date"
              size="small"
              value={checkinDate}
              onChange={(e) => setCheckinDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter:
                    theme.palette.mode === "dark"
                      ? "invert(1)" // white in dark mode
                      : "invert(0)", // normal in light mode
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Check-out Before"
              type="date"
              size="small"
              value={checkoutDate}
              onChange={(e) => setCheckoutDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter:
                    theme.palette.mode === "dark"
                      ? "invert(1)" // white in dark mode
                      : "invert(0)", // normal in light mode
                },
              }}
            />
          </Grid>
          <Grid item xs={12} textAlign="right">
            <Button
              variant="contained"
              onClick={handleFilter}
              disabled={loading}
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Results */}
        {results.length > 0 ? (
          <List>
            {results.map((r) => (
              <React.Fragment key={r._id}>
                <ListItem
                  button
                  onClick={() => handleSelect(r)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    bgcolor:
                      r.pmt_status === "paid"
                        ? `${theme.palette.success.light}33`
                        : `${theme.palette.warning.light}22`,
                    "&:hover": { bgcolor: theme.palette.action.hover },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography fontWeight={600}>
                        {r.room_charges?.[0]?.roomType} — Room{" "}
                        {r.room_charges?.[0]?.roomNumber}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2">
                          Invoice: {r.inv_no}
                        </Typography>
                        <Typography variant="body2">
                          Booking: {r.booking_id}
                        </Typography>
                        <Typography variant="body2">
                          Check-in:{" "}
                          {r.checkin_date
                            ? moment.unix(r.checkin_date).format("DD MMM YYYY")
                            : "-"}{" "}
                          | Check-out:{" "}
                          {r.checkout_date
                            ? moment.unix(r.checkout_date).format("DD MMM YYYY")
                            : "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total: ₹{r.total} — Status: {r.pmt_status}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        ) : (
          !loading && (
            <Box textAlign="center" color="text.secondary" mt={4}>
              <Typography variant="body2">No results found.</Typography>
            </Box>
          )
        )}
      </Drawer>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, invoice: null })}
      >
        <DialogTitle>Confirm Link</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to link <b>{confirmDialog.invoice?.inv_no}</b>{" "}
            (Booking ID: {confirmDialog.invoice?.booking_id}) to this booking
            invoice?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, invoice: null })}
          >
            Cancel
          </Button>
          <Button onClick={confirmLink} variant="contained" color="info">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinkBookingInvoiceButton;
